import { useCallback, useEffect, useState } from 'react';

import {
  listInvitations,
  listMembers,
  listTeams,
} from '../api/tenants.api';
import type { Team, TenantInvitationSummary, TenantMemberSummary } from '../api/tenants.api';
import { useTenant } from '../context/TenantContext';

/**
 * Everything the Team tab renders, for the active workspace.
 *
 * Loaded as one unit because the three lists are mutually dependent on screen: a
 * member row shows the teams they are in, a team row shows its members by name,
 * and an invitation is a member who is not one yet. Fetching them separately
 * would let the page render a team whose members it cannot name.
 *
 * The invitation list is fetched only when the caller may see it. A member gets a
 * 403 for it, and treating that as a page-level error would break the roster they
 * are allowed to read.
 */
export function useTeamData() {
  const { activeTenantId, can } = useTenant();
  const canInvite = can('member.invite');

  const [members, setMembers] = useState<TenantMemberSummary[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [invitations, setInvitations] = useState<TenantInvitationSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    if (!activeTenantId) {
      setMembers([]);
      setTeams([]);
      setInvitations([]);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    void (async () => {
      try {
        const [teamList, memberList] = await Promise.all([
          listTeams(activeTenantId),
          canInvite ? listMembers(activeTenantId) : Promise.resolve([]),
        ]);
        if (cancelled) return;
        setTeams(teamList);
        setMembers(memberList);

        if (canInvite) {
          setInvitations(await listInvitations(activeTenantId));
        } else {
          setInvitations([]);
        }
      } catch (err: unknown) {
        if (cancelled) return;
        setError((err as { message?: string }).message ?? 'Failed to load the workspace');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [activeTenantId, canInvite, nonce]);

  const refresh = useCallback(() => setNonce((n) => n + 1), []);

  return { members, teams, invitations, loading, error, refresh };
}
